/**
 * @fileoverview Honest place-line tokens for Discover v2 list rows (WEB-780).
 *
 * The row's venue · neighborhood meta line is assembled from known tokens
 * only. An unresolved `venueKind` (PIP-87 failed-normalisation address or
 * brand) is treated as an unknown place shape — the raw fallback string is
 * junk and must not render. Blank / whitespace labels are omitted the same
 * way. A dangling ` · ` is never emitted.
 *
 * The accessible name joins the tokens with `, ` so announced copy stays
 * `Title — Venue, Hood` when both resolve.
 *
 * WEB-788 retired the module's visual `venue · neighborhood` formatter: the
 * row's printed facts moved onto the single mono meta line
 * (`lib/discover/rowMetaLine.ts`), which carries the venue, and the hood is
 * printed once in the spine. What remains here is the ANNOUNCED name, which
 * still names both places because a screen-reader listener has no spine to
 * read.
 *
 * @since WEB-780
 */

import { isResolvedVenue, type DiscoverVenueKind } from '@loci/types';

import { resolveNeighborhoodIdentity } from '@/lib/neighborhoods/clt-slugs';

/**
 * The subset of `DiscoverEvent` this module needs. Declared locally so the
 * pure token logic stays decoupled from the full wire contract.
 */
export interface DiscoverRowPlaceInput {
  readonly venueName: string;
  readonly neighborhood: string;
  readonly venueKind?: DiscoverVenueKind;
}

/**
 * Known place tokens for a Discover row, in display order (venue, then
 * neighborhood). Empty when the place shape is fully unknown.
 */
export function discoverRowPlaceTokens(event: DiscoverRowPlaceInput): readonly string[] {
  const venue = isResolvedVenue(event) ? event.venueName.trim() : '';
  const neighborhood = resolveNeighborhoodIdentity(event.neighborhood).displayName;
  const tokens: string[] = [];
  if (venue.length > 0) tokens.push(venue);
  if (neighborhood.length > 0) tokens.push(neighborhood);
  return tokens;
}

/**
 * Row-link accessible name. Same tokens as the visual place line, joined
 * with `, ` so the announced form stays `"Title, Venue, Hood"` when both
 * resolve. Falls back to the title alone when the place shape is unknown.
 */
export function formatDiscoverRowAccessibleName(
  title: string,
  event: DiscoverRowPlaceInput,
): string {
  const tokens = discoverRowPlaceTokens(event);
  return tokens.length > 0 ? `${title}, ${tokens.join(', ')}` : title;
}
